import { memo } from 'react';
import type { FC } from 'react';
import { Divider, Typography } from 'antd';

const { Text, Title } = Typography;

export interface CameraCardContentProps {
  statusText: string;
  weight: number;
  weightPerFish?: number;
  duration: string;
  isCounting: boolean;
  tonnesPerHour: number;
  fishPerHour: number;
  inputTank?: string;
  outputTank?: string;
  isMobile?: boolean;
}

export const CameraCardContent: FC<CameraCardContentProps> = memo(
  ({
    statusText,
    weight,
    weightPerFish,
    duration,
    isCounting,
    tonnesPerHour,
    fishPerHour,
    inputTank,
    outputTank,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for API compatibility
    isMobile: _isMobile = false,
  }) => {
    const formatWeight = (weight: number): string => {
      const floored = Math.floor(weight * 100) / 100;
      return floored.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    // Check if setup is complete (all required fields filled)
    const isSetupComplete = inputTank && outputTank && weightPerFish && weightPerFish > 0;

    // Format tank display text
    const tankDisplayText = isSetupComplete ? `${inputTank} → ${outputTank}` : 'Setup Required';

    // Determine what to show in the top section:
    // - If setup incomplete: show "Setup Required"
    // - When counting (setup complete): show tank info
    // - When not counting (setup complete): show status text (Ready, Starting..., etc.)
    const topSectionText = !isSetupComplete
      ? 'Setup Required'
      : isCounting
        ? tankDisplayText
        : statusText;

    // Check if showing "Setup Required" - needs smaller font to fit on one line
    const isSetupRequired = topSectionText === 'Setup Required';

    // Determine what to show in the count section:
    // - When counting: show the actual count (statusText contains the number)
    // - When not counting: show 0
    const countDisplayText = isCounting ? statusText : '0';

    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          justifyContent: 'space-between',
        }}
      >
        {/* Tank Info / Status */}
        {topSectionText && (
          <>
            <div
              style={{
                textAlign: 'center',
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                paddingBottom: 'clamp(4px, 3cqh, 12px)',
              }}
            >
              <Text
                style={{
                  fontFamily: 'Noto Sans',
                  fontWeight: 700,
                  fontSize: isSetupRequired
                    ? 'clamp(12px, 9.5cqi, 32px)'
                    : 'clamp(14.6px, 11.6cqi, 37.9px)',
                  lineHeight: 'clamp(19.4px, 14.72cqi, 45.4px)',
                  letterSpacing: '0%',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  color: '#111827',
                  display: 'block',
                }}
              >
                {topSectionText}
              </Text>
            </div>
            <Divider
              style={{
                margin: '0',
                borderColor: '#d1d5db',
                flex: '0 0 auto',
              }}
            />
          </>
        )}

        {/* Count & Time */}
        <div
          style={{
            textAlign: 'center',
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: 'clamp(1px, 1cqh, 4px) 0',
          }}
        >
          <Text
            style={{
              fontFamily: 'Noto Sans',
              color: '#6b7280',
              fontSize: 'clamp(10px, 6.82cqi, 26px)',
              marginBottom: 'clamp(0.2px, 0.1cqh, 0.6px)',
              display: 'block',
              fontWeight: 400,
            }}
          >
            Count & Time
          </Text>
          <Title
            level={1}
            style={{
              margin: 0,
              color: '#111827',
              fontFamily: 'Noto Sans',
              fontWeight: 400,
              fontSize: 'clamp(17px, 13.5cqi, 57px)',
              lineHeight: 1.1,
              letterSpacing: '0%',
              textAlign: 'center',
            }}
          >
            {countDisplayText}
          </Title>
          <Text
            style={{
              color: '#1C1C1E',
              fontSize: 'clamp(10px, 6.82cqi, 26px)',
              marginTop: 'clamp(0.2px, 0.1cqh, 0.6px)',
              display: 'block',
              fontFamily: 'Noto Sans',
              textAlign: 'center',
              fontWeight: 'normal',
            }}
          >
            {isCounting ? duration : '00:00:00'}
          </Text>
        </div>

        <Divider
          style={{
            margin: '0',
            borderColor: '#d1d5db',
            flex: '0 0 auto',
          }}
        />

        {/* Weight */}
        <div
          style={{
            textAlign: 'center',
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: 'clamp(1px, 1cqh, 4px) 0',
          }}
        >
          <Text
            style={{
              color: '#6b7280',
              fontSize: 'clamp(10px, 6.82cqi, 26px)',
              marginBottom: 'clamp(0.2px, 0.1cqh, 0.6px)',
              display: 'block',
              fontFamily: 'Noto Sans',
              fontWeight: 400,
            }}
          >
            Weight
          </Text>
          <Title
            level={2}
            style={{
              margin: 0,
              color: '#111827',
              fontFamily: 'Noto Sans',
              fontSize: 'clamp(17px, 13.5cqi, 57px)',
              fontWeight: 400,
              lineHeight: 1.1,
              letterSpacing: '0',
            }}
          >
            {isCounting ? formatWeight(weight) : '0.00'} t
          </Title>
          <Text
            style={{
              color: '#1C1C1E',
              fontSize: 'clamp(10px, 6.82cqi, 26px)',
              marginTop: 'clamp(0.2px, 0.1cqh, 0.6px)',
              display: 'block',
              fontFamily: 'Noto Sans',
              textAlign: 'center',
              fontWeight: 'normal',
            }}
          >
            {weightPerFish ? `${weightPerFish.toFixed(2)} kg/fish` : '-'}
          </Text>
        </div>

        <Divider
          style={{
            margin: '0',
            borderColor: '#d1d5db',
            flex: '0 0 auto',
          }}
        />

        {/* Real-time Speed */}
        <div
          style={{
            textAlign: 'center',
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: 'clamp(1px, 1cqh, 4px) 0',
          }}
        >
          <Text
            style={{
              color: '#6b7280',
              fontFamily: 'Noto Sans',
              fontSize: 'clamp(10px, 6.82cqi, 26px)',
              marginBottom: 'clamp(0.2px, 0.1cqh, 0.6px)',
              display: 'block',
              fontWeight: 400,
            }}
          >
            Real-time Speed
          </Text>
          <Title
            level={2}
            style={{
              margin: 0,
              color: '#111827',
              fontFamily: 'Noto Sans',
              fontSize: 'clamp(17px, 13.5cqi, 57px)',
              fontWeight: 400,
              lineHeight: 1.1,
              letterSpacing: '0',
            }}
          >
            {isCounting ? tonnesPerHour.toFixed(2) : '0.00'} t/h
          </Title>
          <Text
            style={{
              color: '#1C1C1E',
              fontSize: 'clamp(10px, 6.82cqi, 26px)',
              marginTop: 'clamp(0.2px, 0.1cqh, 0.6px)',
              display: 'block',
              fontFamily: 'Noto Sans',
              textAlign: 'center',
              fontWeight: 'normal',
            }}
          >
            {isCounting ? Math.round(fishPerHour).toLocaleString() : '0'} fish/h
          </Text>
        </div>
      </div>
    );
  }
);

CameraCardContent.displayName = 'CameraCardContent';
